"use client";

import { useState, useEffect } from "react";
import {
  User, 
  Shield, 
  Lock, 
  Mail,
  Phone,
  Eye,
  EyeOff,
  Bell,
  ChevronRight,
  MapPin,
  Briefcase,
  Calendar} from "lucide-react";
import { IoMdTime } from "react-icons/io";
import { get, put } from "../utils/service";

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  position: string;
  department: string;
  role: string;
  timezone: string;
  language: string;
  avatar_url: string;
  created_at: string;
  last_login: string;
  gender?: string;
  date_of_birth?: string;
  location?: string;
}

interface ActivityItem {
  id: string;
  user_id: string;
  action: string;
  description: string;
  entity_type: string;
  entity_id: string;
  created_at: string;
  first_name: string;
  last_name: string;
}

interface ActivitiesResponse {
  success: boolean;
  message: string;
  data: {
    activities: ActivityItem[];
  };
  timestamp: string;
}

const SkeletonCard = () => (
  <div className="animate-pulse bg-white rounded-lg p-6">
    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-10 bg-gray-200 rounded"></div>
      ))}
    </div>
  </div>
);

function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [notifications, setNotifications] = useState({
    emailNews: true,
    emailUpdates: true,
    emailResearch: false,
    emailReminders: false,
    pushReminders: true,
    pushActivity: false
  });

  useEffect(() => {
    fetchSettingsData();
  }, []);

  const fetchSettingsData = async () => {
    setLoading(true);
    try {
      const profileResponse = await get('/auth/me');
      if (profileResponse.success) {
        setProfile(profileResponse.data);
      }

      const activitiesResponse = await get('/activities') as ActivitiesResponse;
      if (activitiesResponse.success) {
        setActivities(activitiesResponse.data.activities);
      }
    } catch (error) {
      console.error('Error fetching settings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateProfileField = (field: string, value: string) => {
    if (profile) {
      setProfile(prev => prev ? { ...prev, [field]: value } : null);
    }
  };

  const saveProfileChanges = async () => {
    if (!profile) return;

    setSaving(true);
    try {
      const response = await put('/auth/profile', {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone || '',
        position: profile.position || '',
        department: profile.department || '',
        gender: profile.gender || '',
        date_of_birth: profile.date_of_birth || '',
        location: profile.location || ''
      });

      if (response.success) {
        alert('Profile updated successfully');
      }
    } catch {
      alert('Error updating profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwords.new !== passwords.confirm) {
      alert("New passwords don't match");
      return;
    }

    try {
      const response = await put('/auth/password', {
        current_password: passwords.current,
        new_password: passwords.new,
        confirm_password: passwords.confirm
      });

      if (response.success) {
        alert('Password updated successfully');
        setPasswords({ current: '', new: '', confirm: '' });
      }
    } catch {
      alert('Error updating password');
    }
  };

  const handleNotificationUpdate = async () => {
    try {
      const response = await put('/settings/notifications', notifications);
      if (response.success) {
        alert('Notification preferences updated');
      }
    } catch {
      alert('Error updating notifications');
    }
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const menuItems = [
    { id: 'basic', label: 'Basic Information', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'activities', label: 'Recent Activities', icon: IoMdTime },
    { id: 'security', label: 'Password & Security', icon: Lock }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <SkeletonCard />
            </div>
            <div className="lg:col-span-3">
              <SkeletonCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">

      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg p-4">
              <nav className="space-y-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs transition-colors ${
                        activeTab === item.id
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="flex-1 text-left">{item.label}</span>
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            {activeTab === 'basic' && profile && (
              <div className="space-y-6">
                <div className="bg-black rounded-lg p-6">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">
                        {getInitials(profile.first_name, profile.last_name)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xs font-bold text-white mb-2">
                        {profile.first_name} {profile.last_name}
                      </h2>
                      <p className="text-xs text-gray-300 mb-3">
                        {profile.position || 'Position'} • {profile.department || 'Department'}
                      </p>
                      <div className="flex flex-wrap gap-4 text-xs text-gray-300">
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          <span>{profile.email}</span>
                        </div>
                        {profile.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3" />
                            <span>{profile.phone}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Shield className="w-3 h-3" />
                          <span className="capitalize">{profile.role}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <h2 className="text-xs font-bold text-gray-900 mb-6">Basic Information</h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <User className="w-3 h-3 text-gray-400" />
                        First Name
                      </label>
                      <input
                        type="text"
                        value={profile.first_name}
                        onChange={(e) => updateProfileField('first_name', e.target.value)}
                        placeholder="First Name"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <User className="w-3 h-3 text-gray-400" />
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={profile.last_name}
                        onChange={(e) => updateProfileField('last_name', e.target.value)}
                        placeholder="Last Name"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Mail className="w-3 h-3 text-gray-400" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profile.email}
                        disabled
                        placeholder="email@example.com"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Phone className="w-3 h-3 text-gray-400" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profile.phone || ''}
                        onChange={(e) => updateProfileField('phone', e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <User className="w-3 h-3 text-gray-400" />
                        Gender
                      </label>
                      <select
                        value={profile.gender || ''}
                        onChange={(e) => updateProfileField('gender', e.target.value)}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      >
                        <option value="">Select gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={profile.date_of_birth || ''}
                        onChange={(e) => updateProfileField('date_of_birth', e.target.value)}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Briefcase className="w-3 h-3 text-gray-400" />
                        Position
                      </label>
                      <input
                        type="text"
                        value={profile.position || ''}
                        onChange={(e) => updateProfileField('position', e.target.value)}
                        placeholder="Your Position"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Briefcase className="w-3 h-3 text-gray-400" />
                        Department
                      </label>
                      <input
                        type="text"
                        value={profile.department || ''}
                        onChange={(e) => updateProfileField('department', e.target.value)}
                        placeholder="Your Department"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        Location
                      </label>
                      <input
                        type="text"
                        value={profile.location || ''}
                        onChange={(e) => updateProfileField('location', e.target.value)}
                        placeholder="City, Country"
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={saveProfileChanges}
                      disabled={saving}
                      className="px-6 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? 'Updating...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6">
                  <h2 className="text-xs font-bold text-gray-900 mb-2">Email notification</h2>
                  <p className="text-xs text-gray-500 mb-6">
                    Get emails to find out what's going on when you're not online. You can turn these off.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 mb-1">News</h3>
                        <p className="text-xs text-gray-500">News about products and feature updates.</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifications.emailNews}
                          onChange={(e) => setNotifications(prev => ({ ...prev, emailNews: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>

                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 mb-1">Updates</h3>
                        <p className="text-xs text-gray-500">Nature updates</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifications.emailUpdates}
                          onChange={(e) => setNotifications(prev => ({ ...prev, emailUpdates: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>

                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 mb-1">User research</h3>
                        <p className="text-xs text-gray-500">Get involved in our beta testing program or participate in paid product user research.</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifications.emailResearch}
                          onChange={(e) => setNotifications(prev => ({ ...prev, emailResearch: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>

                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 mb-1">Reminders</h3>
                        <p className="text-xs text-gray-500">These are notifications to remind you of updates you might have missed.</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifications.emailReminders}
                          onChange={(e) => setNotifications(prev => ({ ...prev, emailReminders: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <h2 className="text-xs font-bold text-gray-900 mb-2">Push notification</h2>
                  <p className="text-xs text-gray-500 mb-6">
                    Get push notification in app to find out what's going on when you're online.
                  </p>

                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 mb-1">Reminders</h3>
                        <p className="text-xs text-gray-500">These are notifications to remind you of updates you might have missed.</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifications.pushReminders}
                          onChange={(e) => setNotifications(prev => ({ ...prev, pushReminders: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>

                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xs font-semibold text-gray-900 mb-1">More activity about you</h3>
                        <p className="text-xs text-gray-500">These are notifications for posts on your board, likes and other reactions to your posts, and more.</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={notifications.pushActivity}
                          onChange={(e) => setNotifications(prev => ({ ...prev, pushActivity: e.target.checked }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <button
                      onClick={handleNotificationUpdate}
                      className="px-6 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'activities' && (
              <div className="bg-white rounded-lg p-6">
                <h2 className="text-xs font-bold text-gray-900 mb-6">Recent Activities</h2>

                <div className="space-y-3">
                  {activities.length > 0 ? (
                    activities.map((activity) => {
                      const getActionColor = (action: string) => {
                        if (action.includes('created')) return 'text-primary';
                        if (action.includes('updated')) return 'text-yellow-500';
                        if (action.includes('completed') || action.includes('qualified')) return 'text-green-500';
                        return 'text-white';
                      };

                      const formatDate = (dateString: string) => {
                        const date = new Date(dateString);
                        const now = new Date();
                        const diff = now.getTime() - date.getTime();
                        const hours = Math.floor(diff / (1000 * 60 * 60));
                        const days = Math.floor(hours / 24);

                        if (hours < 24) {
                          return `${hours}h ago`;
                        } else if (days < 7) {
                          return `${days}d ago`;
                        } else {
                          return date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          });
                        }
                      };

                      return (
                        <div key={activity.id} className="flex items-start gap-3 p-4 rounded-lg hover:bg-primary/10 transition-colors">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs font-light rounded-full ${getActionColor(activity.action)}`}>
                                {activity.action.replace('_', ' ').toUpperCase()}
                              </span>
                              <span className="text-xs text-black/60">{formatDate(activity.created_at)}</span>
                            </div>
                            <p className="text-xs text-black font-semibold mb-1">{activity.description}</p>
                            <p className="text-xs text-black/80">
                              By {activity.first_name} {activity.last_name}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">

                        <IoMdTime className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-xs text-gray-900 font-semibold mb-1">No recent activities found</p>
                      <p className="text-xs text-gray-500">Your activities will appear here once you start using the platform</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="bg-white rounded-lg p-6">
                <h2 className="text-xs font-bold text-gray-900 mb-6">Password & Security</h2>

                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Lock className="w-3 h-3 text-gray-400" />
                        Enter Current Password
                      </label>
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwords.current}
                        onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>

                    <div className="relative">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                        <Lock className="w-3 h-3 text-gray-400" />
                        Enter new password
                      </label>
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwords.new}
                        onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
                      <Lock className="w-3 h-3 text-gray-400" />
                      Confirm new password
                    </label>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwords.confirm}
                      onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                      className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handlePasswordChange}
                    disabled={!passwords.current || !passwords.new || !passwords.confirm}
                    className="px-6 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Update
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;